import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import i18n from "../i18n";
import Utils from "../utils/Utils";

const { pathSeparator, serverURL, buildNodeIndex, buildSlugIndex } = Utils;

// Normalize node data from backend: backend uses 'path' for numbered path, we use 'numberedPath'
function normalizeNode(node) {
  if (!node) return node;

  // If node has 'path' but not 'numberedPath', and path looks like numbered path
  if (
    node.path &&
    node.numberedPath === undefined &&
    !node.path.startsWith(pathSeparator)
  ) {
    node.numberedPath = node.path;
  }

  // Recursively normalize children
  if (node.children && Array.isArray(node.children)) {
    node.children = node.children.map(normalizeNode);
  }

  return node;
}

// Find and update a node in the tree by its numbered path or _id
function findAndUpdateNode(rootNode, targetNode, newChildren) {
  if (!rootNode || !targetNode) return false;

  // Check if this is the target node
  if (
    (targetNode.numberedPath &&
      rootNode.numberedPath === targetNode.numberedPath) ||
    (targetNode._id && rootNode._id === targetNode._id)
  ) {
    rootNode.children = newChildren;
    return true;
  }

  // Recursively search children
  if (rootNode.children && Array.isArray(rootNode.children)) {
    for (const child of rootNode.children) {
      if (findAndUpdateNode(child, targetNode, newChildren)) {
        return true;
      }
    }
  }

  return false;
}

const initialState = {
  nodes: {},
  nodesIndex: {}, // Flat index for fast lookups by numberedPath and _id
  slugIndex: {}, // Flat index for fast lookups by slug
  status: "idle",
  error: null,
  searchResult: [],
  searchStatus: "idle",
  suggestions: [],
  suggestionStatus: "idle",
  isAlertMessage: false,
  alertMessage: "",
  alertMessageType: "",
};

export const fetchNodes = createAsyncThunk(
  "nodes/fetchNodes",
  async ({ path, numberedPath, maxDepth, endingPath, noCache }) => {
    // API expects 'path' field with numbered path format (e.g., "001.002")
    // Accept both 'path' and 'numberedPath' for compatibility
    const body = {};

    // Use numberedPath as the primary field, or fall back to path
    if (numberedPath !== undefined) {
      body.path = numberedPath;
    } else if (path !== undefined) {
      // If path looks like a numbered path (e.g., "001.002"), use it directly
      // If it's a legacy name-based path (e.g., "//Tech"), convert or handle specially
      if (path === pathSeparator || path === "" || path === "root") {
        body.path = "root"; // Fetch root node
      } else {
        body.path = path;
      }
    }

    // Add optional parameters
    if (maxDepth !== undefined) body.maxDepth = maxDepth;
    if (endingPath !== undefined) body.endingPath = endingPath;
    if (noCache !== undefined) body.noCache = noCache;

    // Validate: we must send at least a path
    if (!body.path) {
      throw new Error(i18n.t("fetchNodesRequiresPath"));
    }

    const response = await fetch(`${serverURL}/nodes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await response.text());
    const data = await response.json();
    // Normalize the data from backend (backend uses 'path', we use 'numberedPath')
    return normalizeNode(data);
  }
);

export const fetchDeepParent = createAsyncThunk(
  "nodes/fetchDeepParent",
  async ({ path, numberedPath, endingPath }, thunkAPI) => {
    // API expects 'path' field with numbered path format
    const body = {};

    if (numberedPath) {
      body.path = numberedPath;
    } else if (path) {
      // Handle legacy name-based paths
      if (path === pathSeparator || path === "" || path === "root") {
        body.path = "root";
      } else if (path.startsWith(pathSeparator)) {
        // Legacy name-based path - need to convert or handle
        // For now, try to find the node in state and get its numbered path
        const properties = path.split(pathSeparator).filter(Boolean).slice(1);
        const state = thunkAPI.getState();
        let parent = state.nodes.nodes;
        for (const property of properties) {
          const match = parent.children?.find(
            (child) => child.name === property
          );
          if (!match) {
            break;
          }
          parent = match;
        }

        // If we found the parent with numbered path, use it
        if (parent && parent.numberedPath) {
          body.path = parent.numberedPath;
        } else {
          throw new Error(i18n.t("couldNotResolveNumberedPath"));
        }
      } else {
        body.path = path; // Assume it's already a numbered path
      }
    }

    if (endingPath) {
      body.endingPath = endingPath;
    }

    // For deep fetch, we don't want maxDepth limit
    // We want to fetch the entire path down to the target node
    // The backend will handle this with endingPath parameter

    if (!body.path) {
      throw new Error(i18n.t("fetchDeepParentRequiresPath"));
    }

    const response = await fetch(`${serverURL}/nodes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await response.text());
    const data = await response.json();
    // Normalize the data from backend (backend uses 'path', we use 'numberedPath')
    return normalizeNode(data);
  }
);

export const search = createAsyncThunk("nodes/search", async (key) => {
  const response = await fetch(`${serverURL}/nodes/search`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ key }),
  });
  if (!response.ok) throw new Error(await response.text());
  const data = await response.json();

  // Normalize search results (backend uses 'path', frontend uses 'numberedPath')
  const normalizedResults = Array.isArray(data)
    ? data.map((item) => {
        const normalized = { ...item };
        if (
          item.path &&
          !item.numberedPath &&
          !item.path.startsWith(pathSeparator)
        ) {
          normalized.numberedPath = item.path;
        }
        return normalized;
      })
    : [];

  return normalizedResults;
});

export const newSuggestion = createAsyncThunk(
  "nodes/newSuggestion",
  async (suggestion) => {
    // suggestion should now include: { name, parentId (or parent), suggested, searchable }
    // Remove legacy 'path' construction from components
    const response = await fetch(`${serverURL}/suggestions/new`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(suggestion),
    });
    if (!response.ok) throw new Error(await response.text());
    return response.text();
  }
);

export const getSuggestions = createAsyncThunk(
  "nodes/getSuggestions",
  async () => {
    const response = await fetch(`${serverURL}/suggestions`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) throw new Error(await response.text());
    const data = await response.json();
    return data;
  }
);

export const updateSuggestion = createAsyncThunk(
  "nodes/updateSuggestion",
  async ({ id, action, searchable }) => {
    const response = await fetch(`${serverURL}/suggestions/${action}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ id, action, searchable }),
    });
    if (!response.ok) throw new Error(await response.text());
    const data = await response.json();
    return data;
  }
);

const nodesSlice = createSlice({
  name: "nodes",
  initialState,
  reducers: {
    clearAlertMessage: (state) => {
      state.alertMessage = "";
      state.alertMessageType = "";
      state.isAlertMessage = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchNodes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchNodes.fulfilled, (state, action) => {
        const data = action.payload;
        if (data.name) {
          if (data.parent == null) {
            // Root node - replace entire tree
            state.nodes = data;
            // Build index for the entire tree
            state.nodesIndex = buildNodeIndex(data);
            state.slugIndex = buildSlugIndex(state.nodes);
          } else {
            // Non-root node - the API returns the node itself with children populated
            // We need to update this node in our tree and index

            // First, update the node in the tree structure
            const updated = findAndUpdateNode(state.nodes, data, data.children);

            if (updated) {
              // Rebuild the index from the updated tree to ensure consistency
              state.nodesIndex = buildNodeIndex(state.nodes);
              state.slugIndex = buildSlugIndex(state.nodes);
            } else {
              // Node not found in tree - this shouldn't happen, but handle it
              console.warn(
                "Node not found in tree:",
                data.numberedPath || data._id
              );
              // Try to rebuild index anyway
              state.nodesIndex = buildNodeIndex(state.nodes);
              state.slugIndex = buildSlugIndex(state.nodes);
            }
          }
        }
        state.status = "success";
      })
      .addCase(fetchNodes.rejected, (state, action) => {
        state.error = action.error.message;
        state.alertMessage = `${i18n.t("failedFetchingNodes")} ${
          action.error.message
        }`;
        state.alertMessageType = "error";
        state.isAlertMessage = true;
        state.status = "failed";
      });
    builder
      .addCase(fetchDeepParent.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDeepParent.fulfilled, (state, action) => {
        const data = action.payload;
        if (data.name) {
          if (data.parent === null) {
            // Root node
            state.nodes = { ...data };
            state.nodesIndex = buildNodeIndex(data);
            state.slugIndex = buildSlugIndex(state.nodes);
          } else {
            // Deep fetch returns the target node with its full tree
            // Update the node in the tree structure
            const updated = findAndUpdateNode(state.nodes, data, data.children);

            if (updated) {
              // Rebuild the index from the updated tree
              state.nodesIndex = buildNodeIndex(state.nodes);
              state.slugIndex = buildSlugIndex(state.nodes);
            } else {
              // Node not found - rebuild index anyway
              console.warn(
                "Deep parent not found in tree:",
                data.numberedPath || data._id
              );
              state.nodesIndex = buildNodeIndex(state.nodes);
              state.slugIndex = buildSlugIndex(state.nodes);
            }
          }
        }
        state.status = "success";
      })
      .addCase(fetchDeepParent.rejected, (state, action) => {
        state.error = action.error.message;
        state.alertMessage = `${i18n.t("failedFetchingDeepNode")} ${
          action.error.message
        }`;
        state.alertMessageType = "error";
        state.isAlertMessage = true;
        state.status = "failed";
      });
    builder
      .addCase(search.pending, (state) => {
        state.searchStatus = "loading";
      })
      .addCase(search.fulfilled, (state, action) => {
        state.searchResult = action.payload;
        state.searchStatus = "success";
      })
      .addCase(search.rejected, (state, action) => {
        state.error = action.error.message;
        state.alertMessage = `${i18n.t("searchFailed")} ${
          action.error.message
        }`;
        state.alertMessageType = "error";
        state.isAlertMessage = true;
        state.searchStatus = "failed";
      });
    builder
      .addCase(newSuggestion.fulfilled, (state) => {
        state.alertMessage = i18n.t("suggestionSubmitted");
        state.alertMessageType = "success";
        state.isAlertMessage = true;
      })
      .addCase(newSuggestion.rejected, (state, action) => {
        state.alertMessageType = "error";
        state.alertMessage = `${i18n.t("suggestionFailed")} ${
          action.error.message
        }`;
        state.isAlertMessage = true;
      });
    builder
      .addCase(getSuggestions.pending, (state) => {
        state.suggestionStatus = "loading";
      })
      .addCase(getSuggestions.fulfilled, (state, action) => {
        state.suggestions = action.payload;
        state.suggestionStatus = "success";
      })
      .addCase(getSuggestions.rejected, (state, action) => {
        state.error = action.error.message;
        state.alertMessage = `${i18n.t("suggestionLoadingFailed")} ${
          action.error.message
        }`;
        state.alertMessageType = "error";
        state.suggestionStatus = "failed";
        state.isAlertMessage = true;
      });
    builder
      .addCase(updateSuggestion.fulfilled, (state, action) => {
        state.suggestions = action.payload;
        state.suggestionStatus = "success";
      })
      .addCase(updateSuggestion.rejected, (state, action) => {
        state.error = action.error.message;
        state.alertMessage = `${i18n.t("updateFailed")} ${
          action.error.message
        }`;
        state.alertMessageType = "error";
        state.suggestionStatus = "failed";
        state.isAlertMessage = true;
      });
  },
});

export default nodesSlice.reducer;
export const { clearAlertMessage } = nodesSlice.actions;
